import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { CloseCircle } from 'entities/close-circle/model'

import { InsuranceApi } from 'shared/api/insurance'
import { EmptyPage } from 'shared/ui/empty-page'

import { InsuranceCitizenship } from '../../model/types'
import { InsuranceLayout } from '../../ui/insurance-layout'

import { PolicyholderForm } from './ui/policyholder-form'
import { PolicyholderSkeleton } from './ui/policyholder-skeleton'

interface PolicyholderInsurancePageProps {
  citizenships: InsuranceCitizenship[] | null
  closeCircles: CloseCircle[] | null
  onCitizenshipsLoaded: (citizenships: InsuranceCitizenship[]) => void
  loadCloseCircles: () => Promise<CloseCircle[] | null>
}

const PolicyholderInsurancePage = ({
  citizenships,
  closeCircles,
  onCitizenshipsLoaded,
  loadCloseCircles,
}: PolicyholderInsurancePageProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(citizenships === null)
  const [hasError, setHasError] = useState(false)
  const [retryCount, setRetryCount] = useState(0)

  useEffect(() => {
    if (citizenships !== null) {
      setIsLoading(false)
      setHasError(false)
      return
    }

    const abortController = new AbortController()

    setIsLoading(true)
    setHasError(false)

    InsuranceApi.getCitizenships(abortController.signal)
      .then(({ data }) => onCitizenshipsLoaded(data.data))
      .catch(() => {
        if (!abortController.signal.aborted) {
          setHasError(true)
        }
      })
      .finally(() => {
        if (!abortController.signal.aborted) {
          setIsLoading(false)
        }
      })

    return () => abortController.abort()
  }, [citizenships, onCitizenshipsLoaded, retryCount])

  return isLoading ? (
    <InsuranceLayout
      title={t('insurance.vzr.policyholder.title')}
      onGoBack={() => navigate(-1)}
    >
      <PolicyholderSkeleton />
    </InsuranceLayout>
  ) : hasError ? (
    <InsuranceLayout
      title={t('insurance.vzr.policyholder.title')}
      onGoBack={() => navigate(-1)}
    >
      <div className="container">
        <EmptyPage
          title={t('insurance.vzr.errors.load_title')}
          subTitle={t('error_des')}
          buttonText={t('try_again')}
          onClick={() => setRetryCount((current) => current + 1)}
        />
      </div>
    </InsuranceLayout>
  ) : (
    <PolicyholderForm
      citizenships={citizenships || []}
      closeCircles={closeCircles}
      loadCloseCircles={loadCloseCircles}
    />
  )
}

export default PolicyholderInsurancePage
